import { getSafeRedirectPath } from "@/libs/redirect";
import type { SessionBootstrapResponse, SessionNextStep } from "./types";

export const getNextStepPath = (
  nextStep: SessionNextStep | null | undefined,
): string | null => {
  switch (nextStep) {
    case "complete_personal_profile":
      return "/auth/onboarding";
    case "complete_workspace_onboarding":
    case "complete_company_onboarding":
    case "convert_solo_freelancer_to_company":
      return "/auth/onboarding/company";
    case "create_solo_freelancer_workspace":
      return "/job-offers/create/start";
    case "reactivate_solo_freelancer":
      return "/profile";
    case "publisher_dashboard":
      return "/profile/company";
    // La dashboard freelance, non il profilo: è la pagina che risponde alla
    // domanda con cui un freelance torna («mi sta cercando qualcuno?»). Il
    // profilo resta raggiungibile e resta nella sidebar, ma è il posto dove si
    // *scrive*, non dove si guarda. `resolveSessionRedirectPath` continua a far
    // vincere una destinazione esplicita su questa (EC18).
    case "candidate_dashboard":
      return "/freelance/dashboard";
    // Non `/auth/signup`: chi riceve questo `next_step` è già registrato e
    // loggato: rimandarlo alla registrazione sarebbe un vicolo cieco. La
    // domanda va posta dov'è ancora utile, cioè dentro la sessione.
    case "choose_intent":
      return "/auth/intent";
    default:
      return null;
  }
};

/**
 * A destination worth carrying, or nothing.
 *
 * `"/"` counts as nothing on purpose: it is both what `getSafeRedirectPath`
 * returns when it rejects a value **and** a destination that would be reached
 * anyway. Collapsing the two keeps the caller from having to tell a refused
 * open-redirect apart from a pointless one.
 */
export const carriableDestination = (
  preferredPath: string | null | undefined,
): string | null => {
  if (!preferredPath) return null;
  const resolved = getSafeRedirectPath(preferredPath, "/");
  return resolved === "/" ? null : resolved;
};

/**
 * The link from login to signup, with whatever the visitor was trying to reach.
 *
 * This is the hop the destination used to die on. An anonymous visitor clicks
 * "Candidati" on a public company page, the modal sends them to
 * `/auth?next=/job-offers/<id>/apply`, and login carries that `next` correctly —
 * but the two "create an account" links next to it dropped it, so anyone
 * **without** an account (the exact audience the public page exists to convert)
 * finished onboarding on their dashboard with no way back to the offer, and no
 * message (AC 14/I11).
 *
 * `email` and `next` are independent: either, both, or neither.
 */
export const buildSignupHref = (options: {
  email?: string | null;
  next?: string | null;
}): string => {
  const params = new URLSearchParams();

  if (options.email) params.set("email", options.email);

  // Sanitized here rather than at the destination: `SignUpRoute` re-validates,
  // but a refused value must not travel as far as the address bar in the first
  // place.
  const destination = carriableDestination(options.next);
  if (destination) params.set("next", destination);

  const query = params.toString();
  return query ? `/auth/signup?${query}` : "/auth/signup";
};

/**
 * Where a session lands, and — for one `next_step` — what it takes along.
 *
 * The two dashboard steps have always honoured `preferredPath`, because there
 * the destination *is* the landing. `complete_personal_profile` used to throw it
 * away, and nobody picked it up again: someone who clicked "Candidati" on a
 * public company page as an anonymous visitor and chose to **register** lost the
 * offer at the door, without a message (I11/EP7). It is the main conversion path
 * of that page, broken precisely for new users.
 *
 * So the destination is handed to onboarding as a query param and consumed by
 * its closing screen. Only that step: `next` travels where a closure exists that
 * can spend it, otherwise it would sit in URLs collecting stale paths.
 */
export const resolveSessionRedirectPath = (
  context: Pick<SessionBootstrapResponse, "next_step"> | null | undefined,
  preferredPath?: string,
) => {
  const nextStepPath = getNextStepPath(context?.next_step);

  if (
    preferredPath &&
    (context?.next_step === "candidate_dashboard" ||
      context?.next_step === "publisher_dashboard")
  ) {
    return getSafeRedirectPath(preferredPath, nextStepPath ?? "/");
  }

  if (context?.next_step === "complete_personal_profile" && nextStepPath) {
    const destination = carriableDestination(preferredPath);
    // Encoded, not concatenated: the destination carries its own `?job=…`, and
    // an unescaped `?` would make the second query string swallow the first.
    if (destination)
      return `${nextStepPath}?next=${encodeURIComponent(destination)}`;
  }

  return nextStepPath ?? getSafeRedirectPath(preferredPath, "/");
};

/**
 * What the onboarding closure should do with the `next` it was handed.
 *
 * Re-validated here rather than trusted: between the redirect and the closing
 * screen the value has been through the address bar, where anyone can rewrite
 * it.
 */
export const resolveOnboardingReturnPath = (
  next: string | null | undefined,
): string | null => carriableDestination(next);
